import { Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useShowCostAmounts } from '~/hooks/useCanSeeCost';
import { cn } from '~/utils/cn';

import { useCostEstimate } from '../hooks/use-cost-estimate';
import { formatUsd } from '../utils/format-usd';

import { CostApprovalWaitingNote } from './cost-approval-waiting';

interface CostApprovalPendingBannerProps {
  workflowId: string | null | undefined;
  /** Host run is live / ended — same meaning as on `CostEstimateCard`, so the shared query
   *  stops polling once the run ends. Omit when the host cannot tell. */
  runActive?: boolean | undefined;
  className?: string;
  /** `data-testid` root; the message line gets `${testId}-message`. */
  testId?: string;
}

/**
 * "Waiting for cost approval" strip for the pipeline timeline — shares the
 * card's query, so it adds no request and shows only while the gate is pending.
 * With amounts hidden (server `amountsHidden`, else a customer viewer) it reads "being
 * reviewed by Lily", with no amount.
 */
export function CostApprovalPendingBanner({
  workflowId,
  runActive,
  className,
  testId = 'cost-approval-pending-banner',
}: CostApprovalPendingBannerProps) {
  const { t } = useTranslation('common');
  const { data: estimate } = useCostEstimate(workflowId, { runActive });
  const showAmounts = useShowCostAmounts(estimate?.amountsHidden);
  if (estimate?.status !== 'PENDING_APPROVAL') return null;
  return (
    <div
      role="status"
      data-testid={testId}
      className={cn(
        'bg-warning-50 text-warning flex w-full items-start gap-2 rounded-lg px-4 py-3 text-sm',
        className,
      )}
    >
      <Pause className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="font-medium">{t('costEstimate.banner.title')}</p>
        <p data-testid={`${testId}-message`}>
          {!showAmounts
            ? t('costEstimate.banner.customerMessage')
            : estimate.approvalTier === 'APPROVERS'
              ? estimate.total
                ? t('costEstimate.banner.message', { total: formatUsd(estimate.total.avgUsd) })
                : t('costEstimate.banner.messageNoTotal')
              : estimate.total
                ? t('costEstimate.banner.selfMessage', { total: formatUsd(estimate.total.avgUsd) })
                : t('costEstimate.banner.selfMessageNoTotal')}
        </p>
        <CostApprovalWaitingNote
          waiting={{ waitingSince: estimate.createdAt, workflowId: estimate.workflowId }}
          className="text-xs"
        />
      </div>
    </div>
  );
}
